import { Head, useForm } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SettingsMenuButton, SettingsNavigation } from '@/components/settings/SettingsNavigation'
import type { SettingsAccount, SettingsIdentity, SettingsUser } from '@/types'

type Props = {
  account: SettingsAccount
  user: SettingsUser
  identity: SettingsIdentity
}

export default function SettingsIndex({ account, user, identity }: Props) {
  const profileForm = useForm({
    name: user.name,
    email: identity.email_address
  })

  const workspaceForm = useForm({
    name: account.name
  })

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    profileForm.patch(`/${account.slug}/profile`)
  }

  const handleWorkspaceSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  return (
    <DashboardLayout
      title="Settings"
      navigation={<SettingsNavigation active="general" />}
      mobileGuide={<SettingsMenuButton active="general" />}
    >
      <Head title="Settings" />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your space and your preferences
          </p>
        </div>

        {/* Profile Settings */}
        <div className="pb-8 border-b border-border/40">
          <div className="mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Update your personal information and email preferences
            </p>
          </div>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="profile-name" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Full Name</Label>
                <Input
                  id="profile-name"
                  value={profileForm.data.name}
                  onChange={e => profileForm.setData('name', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="profile-email" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Email Address</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileForm.data.email}
                  onChange={e => profileForm.setData('email', e.target.value)}
                  disabled
                  className="h-8 text-sm bg-muted"
                />
                <p className="text-[12px] text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <Button
                type="submit"
                size="sm"
                className="h-8 text-[13px]"
                disabled={profileForm.processing}
              >
                Save Changes
              </Button>
              {profileForm.recentlySuccessful && (
                <p className="text-[13px] text-primary">Profile updated!</p>
              )}
            </div>
          </form>
        </div>

        {/* Space settings */}
        <div className="pb-8">
          <div className="mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Space</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Manage this space's name and URL
            </p>
          </div>
          <form onSubmit={handleWorkspaceSubmit} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <Label htmlFor="workspace-name" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Space name</Label>
              <Input
                id="workspace-name"
                value={workspaceForm.data.name}
                onChange={e => workspaceForm.setData('name', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Space URL</Label>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md text-[13px]">
                <span className="text-muted-foreground">app.corppy.com/</span>
                <span className="font-medium">{account.slug}</span>
              </div>
              <p className="text-[12px] text-muted-foreground">
                This is this space's unique identifier
              </p>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <Button
                type="submit"
                size="sm"
                className="h-8 text-[13px]"
                disabled={workspaceForm.processing}
              >
                Update space
              </Button>
              <p className="text-[12px] text-muted-foreground">
                Created {new Date(account.created_at).toLocaleDateString()}
              </p>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  )
}
